import { useRef, useState, type ReactElement } from "react";
import { MoreHorizontal, type LucideIcon } from "lucide-react";
import { space } from "@tokens/index";
import { Button, IconButton, Menu, MenuItem, useViewport } from "@primitives/index";

/**
 * RowActions: the controls at the end of a data row.
 *
 * `expanded`+ and `medium` render inline ghost buttons. `compact` with
 * two or more actions collapses them into a kebab `Menu` so the row title
 * keeps its width. The kebab sits at the top of the title line.
 */

export interface RowAction {
    readonly key: string;
    readonly label: string;
    readonly icon?: LucideIcon;
    readonly onSelect: () => void;
    readonly disabled?: boolean;
    readonly tone?: "default" | "danger";
    /** Inline variant. Menu items ignore this. Default `ghost`. */
    readonly variant?: "ghost" | "primary" | "secondary";
}

interface RowActionsProps {
    readonly actions: readonly RowAction[];
    /** Accessible name of the kebab button. */
    readonly menuLabel: string;
}

export function RowActions({ actions, menuLabel }: RowActionsProps): ReactElement | null {
    const { isCompact } = useViewport();
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);

    if (actions.length === 0) return null;

    if (isCompact && actions.length >= 2) {
        return (
            <>
                <IconButton
                    ref={anchorRef}
                    icon={MoreHorizontal}
                    aria-label={menuLabel}
                    tooltip={menuLabel}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    onClick={() => setOpen((value) => !value)}
                />
                <Menu
                    open={open}
                    onClose={() => setOpen(false)}
                    anchorRef={anchorRef}
                    placement="bottom-end"
                >
                    {actions.map((action) => {
                        const Icon = action.icon;
                        return (
                            <MenuItem
                                key={action.key}
                                label={action.label}
                                icon={
                                    Icon !== undefined ? (
                                        <Icon size={16} strokeWidth={2} />
                                    ) : undefined
                                }
                                onSelect={action.onSelect}
                                disabled={action.disabled ?? false}
                                tone={action.tone ?? "default"}
                            />
                        );
                    })}
                </Menu>
            </>
        );
    }

    return (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: space[2] }}>
            {actions.map((action) => {
                const Icon = action.icon;
                return (
                    <Button
                        key={action.key}
                        variant={action.variant ?? "ghost"}
                        size="sm"
                        onClick={action.onSelect}
                        disabled={action.disabled ?? false}
                    >
                        {Icon !== undefined && (
                            <Icon size={14} strokeWidth={2.25} aria-hidden="true" />
                        )}
                        {action.label}
                    </Button>
                );
            })}
        </div>
    );
}
